import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Popover,
  TextField,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
  InputAdornment,
} from "@mui/material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import IntegrationService from "../../services/integrations/Integrations";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import useGlobalContext from "../../context/useGlobalContext";
import { useQueryClient } from "@tanstack/react-query";
import "../../styles/analytics.css";
import DeleteIcon from "../../assets/analytics/IcTrash.svg";
import EditColumnIcon from "../../assets/action-items/edit-column-icon.svg";
import SearchFilterIcon from "../../assets/common/search-filter-icon.svg";
import AccountsTimeLineViewIcon from "../../assets/accounts/accounts_time_line_icon.svg";
import AccountsSummaryViewIcon from "../../assets/accounts/accounts_summary_view_icon.svg";
import {
  isCurrentMonthRange,
  parseSavedViewsMap,
} from "./MMNAReport";

export interface SavedViewsProps {
  salesforceReportPref?: Record<string, any> | null;
  preferenceKey?: "salesforce_report" | "customer_preferences";
  activeFilterCount: number;
  visibleColumnCount: number;
  hasUnsavedChanges: boolean;
  activeViewName: string | null;
  currentConfigSnapshot: any;
  onSelectView: (viewName: string, viewConfig: any) => void;
  onClearAllFilters?: () => void;
  onSaveSuccess?: () => void;
  onDeleteSuccess?: (deletedViewName: string) => void;
  getFilterCount?: (config: any) => number;
  getColumnCount?: (config: any) => number;
}

const getFilterCountForConfig = (config: any) => {
  if (!config || typeof config !== "object") return 0;
  let count = 0;
  if (config?.stages?.length || config?.stage?.length) count++;
  if (config?.ae_teams?.length || config?.ae_team?.length) count++;
  if (config?.deal_types?.length || config?.deal_type?.length) count++;
  if (config?.record_types?.length || config?.record_type?.length) count++;
  if (config?.manager_forecast?.length || config?.manager_forecasts?.length) count++;
  if (config?.buyer_modes?.length || config?.buyer_mode?.length) count++;
  if (config?.l1_rygs?.length || config?.l1_ryg?.length) count++;
  if (config?.opportunity_sources?.length || config?.opportunity_source?.length) count++;
  if (config?.close_date_start && config?.close_date_end) {
    if (!isCurrentMonthRange(config.close_date_start, config.close_date_end)) {
      count++;
    }
  }
  if (config?.created_date_start && config?.created_date_end) count++;
  if (config?.arr_amount !== undefined && config?.arr_amount !== null && config?.arr_amount_operator) count++;
  if (config?.opportunity_ids || config?.opportunity_name) count++;
  if (config?.market_pod_lead) count++;
  if (config?.opportunity_owner) count++;
  if (config?.account_ids || config?.account_name) count++;
  if (config?.india_sp) count++;
  if (config?.sp_pod_lead) count++;
  return count;
};

const getColCountForConfig = (config: any) => {
  if (!config || typeof config !== "object") return 0;
  if (config?.columns && typeof config.columns === "object" && !Array.isArray(config.columns)) {
    return Object.values(config.columns).filter(Boolean).length;
  }
  if (Array.isArray(config?.columns)) {
    return config.columns.length;
  }
  return 0;
};

const SaveViewPreferences: React.FC<SavedViewsProps> = ({
  salesforceReportPref,
  preferenceKey = "salesforce_report",
  activeFilterCount,
  visibleColumnCount,
  hasUnsavedChanges,
  activeViewName,
  currentConfigSnapshot,
  onSelectView,
  onClearAllFilters,
  onSaveSuccess,
  onDeleteSuccess,
  getFilterCount,
  getColumnCount,
}) => {
  const queryClient = useQueryClient();
  const { accountDetails } = useGlobalContext();
  const employeePorId = accountDetails?.profileOrgId ?? "";
  const { showSnackbar } = useDisplaySnackbar();

  const { mutate: updatePreferences, isPending: isSavingPreferences } =
    IntegrationService.useUpdateUserPreferences();
  const preferenceField = preferenceKey || "salesforce_report";

  const isCustomerPreference = preferenceKey === "customer_preferences";

  const preferenceLabels = {
    singular: isCustomerPreference ? "view" : "report",
    plural: isCustomerPreference ? "Views" : "Reports",
    save: isCustomerPreference ? "Save this view" : "Save this report",
    name: isCustomerPreference ? "Name this view" : "Name this report",
    saveAction: isCustomerPreference ? "Save View" : "Save Report",
    clearDescription: isCustomerPreference
      ? "Resets to default view"
      : "Resets to default report",
    nameDescription: isCustomerPreference
      ? "Saves your active filters & column layout as a reusable view."
      : "Saves your active filters & column layout as a reusable report.",
  };
  const customerViewModeBadge = !isCustomerPreference
    ? null
    : currentConfigSnapshot?.view === "summary"
      ? { label: "Summary View", icon: AccountsSummaryViewIcon }
      : currentConfigSnapshot?.view === "timeline"
        ? { label: "Timeline View", icon: AccountsTimeLineViewIcon }
        : null;

  const writeSavedViewsCache = useCallback((views: Record<string, any>) => {
    queryClient.setQueryData(
      ["get_user_preferences", { employeePorId }],
      (prevData: any) => ({
        ...(prevData || {}),
        userPreferences: {
          ...(prevData?.userPreferences || {}),
          [preferenceField]: views,
        },
      })
    );
    queryClient.invalidateQueries({
      queryKey: ["get_user_preferences", { employeePorId }],
    });
  }, [employeePorId, preferenceField, queryClient]);

  // Saved Views Map
  const savedViewsMap = useMemo(() => {
    return parseSavedViewsMap(salesforceReportPref);
  }, [salesforceReportPref]);

  // Saved Views UI States
  const [savedViewsAnchorEl, setSavedViewsAnchorEl] = useState<HTMLElement | null>(null);
  const [savedViewStep, setSavedViewStep] = useState<"list" | "name">("list");
  const [newViewName, setNewViewName] = useState<string>("");
  const [editingInlineViewName, setEditingInlineViewName] = useState<string | null>(null);
  const [inlineViewNameInput, setInlineViewNameInput] = useState<string>("");

  const handleClosePopover = useCallback(() => {
    setSavedViewsAnchorEl(null);
    setSavedViewStep("list");
    setNewViewName("");
    setEditingInlineViewName(null);
  }, []);

  const handleUpdateActiveView = useCallback(() => {
    if (!activeViewName || !employeePorId) return;

    const updatedSalesforceReport = {
      ...savedViewsMap,
      [activeViewName]: currentConfigSnapshot,
    };

    updatePreferences(
      {
        params: { employeePorId },
        payload: {
          [preferenceField]: updatedSalesforceReport,
        },
      },
      {
        onSuccess: () => {
          showSnackbar("success", `Updated "${activeViewName}" successfully!`);
          writeSavedViewsCache(updatedSalesforceReport);
          onSelectView(activeViewName, currentConfigSnapshot);
          if (onSaveSuccess) onSaveSuccess();
          handleClosePopover();
        },
        onError: (err: any) => {
          showSnackbar("error", err?.message || "Failed to update report.");
        },
      }
    );
  }, [
    activeViewName,
    employeePorId,
    currentConfigSnapshot,
    savedViewsMap,
    updatePreferences,
    showSnackbar,
    writeSavedViewsCache,
    preferenceField,
    onSelectView,
    onSaveSuccess,
    handleClosePopover,
  ]);

  const handleSaveInlineRename = useCallback(
    (oldName: string) => {
      const newName = inlineViewNameInput.trim();
      if (!newName || !employeePorId) return;

      if (oldName === newName) {
        setEditingInlineViewName(null);
        return;
      }

      if (savedViewsMap[newName]) {
        showSnackbar("error", `A report named "${newName}" already exists.`);
        return;
      }

      updatePreferences(
        {
          params: { employeePorId },
          payload: {
            [preferenceField]: {
              [oldName]: {
                rename_to: newName,
              },
            },
          },
        },
        {
          onSuccess: () => {
            showSnackbar("success", "Report renamed successfully!");
            const updatedSalesforceReport = { ...savedViewsMap };
            const existingConfig = updatedSalesforceReport[oldName];
            delete updatedSalesforceReport[oldName];
            updatedSalesforceReport[newName] = existingConfig;

            writeSavedViewsCache(updatedSalesforceReport);
            if (activeViewName === oldName) {
              onSelectView(newName, existingConfig);
            }
            setEditingInlineViewName(null);
          },
          onError: (err: any) => {
            showSnackbar("error", err?.message || "Failed to rename the report.");
          },
        }
      );
    },
    [
      inlineViewNameInput,
      employeePorId,
      savedViewsMap,
      updatePreferences,
      showSnackbar,
      writeSavedViewsCache,
      preferenceField,
      activeViewName,
      onSelectView,
    ]
  );

  const handleSaveNewView = useCallback(() => {
    if (!newViewName.trim() || !employeePorId) return;

    const name = newViewName.trim();
    if (savedViewsMap[name]) {
      showSnackbar("error", `A report named "${name}" already exists.`);
      return;
    }

    const updatedSalesforceReport = { ...savedViewsMap };
    updatedSalesforceReport[name] = currentConfigSnapshot;

    updatePreferences(
      {
        params: { employeePorId },
        payload: {
          [preferenceField]: updatedSalesforceReport,
        },
      },
      {
        onSuccess: () => {
          showSnackbar("success", "Report saved successfully!");
          writeSavedViewsCache(updatedSalesforceReport);
          onSelectView(name, currentConfigSnapshot);
          if (onSaveSuccess) onSaveSuccess();
          handleClosePopover();
        },
        onError: (err: any) => {
          showSnackbar("error", err?.message || "Failed to save report.");
        },
      }
    );
  }, [
    newViewName,
    employeePorId,
    currentConfigSnapshot,
    savedViewsMap,
    updatePreferences,
    showSnackbar,
    writeSavedViewsCache,
    preferenceField,
    onSelectView,
    onSaveSuccess,
    handleClosePopover,
  ]);

  const handleDeleteSavedView = useCallback(
    (nameToDelete: string) => {
      if (!employeePorId) return;

      updatePreferences(
        {
          params: { employeePorId },
          payload: {
            [preferenceField]: {
              [nameToDelete]: {
                delete_view: true,
              },
            },
          },
        },
        {
          onSuccess: () => {
            showSnackbar("success", "Report deleted successfully!");
            const updatedSalesforceReport = { ...savedViewsMap };
            delete updatedSalesforceReport[nameToDelete];

            writeSavedViewsCache(updatedSalesforceReport);

            if (activeViewName === nameToDelete) {
              const remainingNames = Object.keys(updatedSalesforceReport);
              if (isCustomerPreference && remainingNames.length > 0) {
                const nextName = remainingNames[0];
                onSelectView(nextName, updatedSalesforceReport[nextName]);
              } else if (onClearAllFilters) {
                onClearAllFilters();
              }
            }

            if (onDeleteSuccess) onDeleteSuccess(nameToDelete);
          },
          onError: (err: any) => {
            showSnackbar("error", err?.message || "Failed to delete report.");
          },
        }
      );
    },
    [
      employeePorId,
      savedViewsMap,
      updatePreferences,
      showSnackbar,
      writeSavedViewsCache,
      preferenceField,
      activeViewName,
      isCustomerPreference,
      onSelectView,
      onClearAllFilters,
      onDeleteSuccess,
    ]
  );

  // Button Label & Color determination
  const buttonLabel = useMemo(() => {
    if (activeViewName) {
      if (hasUnsavedChanges) return `Update "${activeViewName}"`;
      return activeViewName;
    }

    return preferenceLabels.save;
  }, [activeViewName, hasUnsavedChanges, preferenceLabels.save]);

  const isButtonDirty = hasUnsavedChanges;
  const isButtonActiveClean = Boolean(activeViewName && !hasUnsavedChanges);

  return (
    <>
      <Button
        variant="contained"
        disabled={isSavingPreferences}
        onClick={(e) => setSavedViewsAnchorEl(e.currentTarget)}
        className={`save-view-pref-btn ${
          isButtonDirty
            ? "save-view-pref-btn--dirty"
            : isButtonActiveClean
            ? "save-view-pref-btn--active"
            : ""
        }`}
      >
        <Box className="save-view-pref-btn__icon-wrap">
          {isButtonActiveClean ? (
            <CheckIcon sx={{ fontSize: 16, color: "#6036D6" }} />
          ) : (
            <BookmarkBorderIcon sx={{ fontSize: 16, color: isButtonDirty ? "#FFFFFF" : "#5731C3" }} />
          )}
        </Box>
        <Typography
          className="save-view-pref-btn__text"
          sx={{ color: isButtonDirty ? "#FFFFFF" : "#5731C3" }}
        >
          {buttonLabel}
        </Typography>
        {isButtonDirty && <Box className="save-view-pref-btn__dot" />}
        <KeyboardArrowDownIcon
          sx={{ fontSize: 18, color: isButtonDirty ? "#FFFFFF" : "#A78BFA" }}
        />
      </Button>

      <Popover
        open={Boolean(savedViewsAnchorEl)}
        anchorEl={savedViewsAnchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          className: "save-view-pref-popover",
        }}
      >
        {/* Header */}
        <Box className="save-view-pref-header">
          <Box>
            <Typography className="save-view-pref-header__title">
              Saved {preferenceLabels.plural}
            </Typography>
            <Typography className="save-view-pref-header__subtitle">
              Saves your active filters & column layout
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleClosePopover}
            className="save-view-pref-header__close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {savedViewStep === "list" ? (
          <Box className="save-view-pref-list">
            {/* View Changes Detected Banner (Image 1) */}
            {activeViewName && hasUnsavedChanges && (
              <Box className="save-view-pref-banner">
                <Box sx={{ flex: 1 }}>
                  <Typography className="save-view-pref-banner__text">
                    Updates to <strong style={{ color: "#6036D6" }}>"{activeViewName}"</strong> are unsaved, click Update to save.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleUpdateActiveView}
                  disabled={isSavingPreferences}
                  className="save-view-pref-banner__btn"
                >
                  {isSavingPreferences ? <CircularProgress size={14} color="inherit" /> : "Update"}
                </Button>
              </Box>
            )}

            {/* Divider line before scroll area */}
            <Divider sx={{ borderColor: "#EDE9FE" }} />

            {/* Scrollable Container for Clear Row + Saved Views List */}
            <Box className="save-view-pref-scroll-area">
              {/* Clear All Filters Row */}
              <Box
                onClick={() => {
                  if (onClearAllFilters) onClearAllFilters();
                  handleClosePopover();
                }}
                className="save-view-pref-clear-row"
              >
                <Box className="save-view-pref-clear-row__icon">
                  <CloseIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography className="save-view-pref-clear-row__title">
                    Clear all filters
                  </Typography>
                  <Typography className="save-view-pref-clear-row__sub">
                    {preferenceLabels.clearDescription}
                  </Typography>
                </Box>
              </Box>

              {/* Saved Views List */}
              {Object.keys(savedViewsMap).length > 0 && (
                <Box className="save-view-pref-section">
                  {Object.entries(savedViewsMap).map(([viewName, viewConfig]: [string, any]) => {
                    const isActive = activeViewName === viewName;
                    const isInlineEditing = editingInlineViewName === viewName;
                    const filterCnt = getFilterCount
                      ? getFilterCount(viewConfig)
                      : getFilterCountForConfig(viewConfig);
                    const colCnt = getColumnCount
                      ? getColumnCount(viewConfig)
                      : getColCountForConfig(viewConfig);
                    const viewModeLabel = !isCustomerPreference
                      ? ""
                      : viewConfig?.view === "summary"
                        ? "Summary View"
                        : viewConfig?.view === "timeline"
                          ? "Timeline View"
                          : "";
                    return (
                      <Box
                        key={viewName}
                        onClick={() => {
                          if (!isInlineEditing) {
                            if (viewName !== activeViewName) {
                              onSelectView(viewName, viewConfig);
                            }
                            handleClosePopover();
                          }
                        }}
                        className={`save-view-pref-item ${isActive ? "save-view-pref-item--active" : ""}`}
                      >
                        <Box className="save-view-pref-item__content">
                          {isActive ? (
                            <Box className="save-view-pref-active-icon">
                              <CheckIcon sx={{ fontSize: 18 }} />
                            </Box>
                          ) : (
                            <Box className="save-view-pref-inactive-icon">
                              <ShowChartIcon sx={{ fontSize: 18 }} />
                            </Box>
                          )}
                          <Box className="save-view-pref-item__text-wrap">
                            {isInlineEditing ? (
                              <Box
                                onClick={(e) => e.stopPropagation()}
                                className="save-view-pref-inline-edit"
                              >
                                <TextField
                                  size="small"
                                  value={inlineViewNameInput}
                                  onChange={(e) => setInlineViewNameInput(e.target.value.slice(0, 25))}
                                  inputProps={{ maxLength: 25 }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Typography
                                          variant="caption"
                                          sx={{ color: "#94A3B8", fontSize: "10px", userSelect: "none" }}
                                        >
                                          {inlineViewNameInput.length}/25
                                        </Typography>
                                      </InputAdornment>
                                    ),
                                  }}
                                  autoFocus
                                  disabled={isSavingPreferences}
                                  placeholder="View name"
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleSaveInlineRename(viewName);
                                    } else if (e.key === "Escape") {
                                      setEditingInlineViewName(null);
                                    }
                                  }}
                                  className="save-view-pref-inline-input"
                                />
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveInlineRename(viewName);
                                  }}
                                  disabled={!inlineViewNameInput.trim() || isSavingPreferences}
                                  className="save-view-pref-inline-save-btn"
                                >
                                  {isSavingPreferences ? <CircularProgress size={12} color="inherit" /> : "Save"}
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingInlineViewName(null);
                                  }}
                                  className="save-view-pref-inline-cancel-btn"
                                >
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            ) : (
                              <>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "6px", maxWidth: "100%" }}>
                                  <Typography
                                    noWrap
                                    className={`save-view-pref-item__name ${isActive ? "save-view-pref-item__name--active" : ""}`}
                                    sx={{ maxWidth: "170px" }}
                                  >
                                    {viewName}
                                  </Typography>
                                  {isActive && hasUnsavedChanges && (
                                    <Box className="save-view-pref-btn__dot" />
                                  )}
                                </Box>
                              </>
                            )}
                              <Typography className="save-view-pref-item__meta">
                                {viewModeLabel ? `${viewModeLabel} • ` : ""}{filterCnt} filters • {colCnt} columns
                              </Typography>
                          </Box>
                        </Box>

                        {!isInlineEditing && (
                          <Box className="save-view-pref-item__actions">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingInlineViewName(viewName);
                                setInlineViewNameInput(viewName);
                              }}
                              className="save-view-pref-item__actions-border"
                              sx={{ color: "#9E9EAE", "&:hover": { color: "#6036D6" }, p: 0.5 }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSavedView(viewName);
                              }}
                              className="save-view-pref-item__actions-border save-view-pref-item__actions-border--delete"
                            >
                              <img src={DeleteIcon}/>
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Divider line after scroll area */}
            <Divider sx={{ borderColor: "#EDE9FE" }} />

            {/* + Save this view Card at the END of popover */}
            <Box
              onClick={() => {
                setNewViewName("");
                setSavedViewStep("name");
              }}
              className="save-view-pref-add-card"
            >
              <Box className="save-view-pref-add-card__icon">
                <AddIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography className="save-view-pref-add-card__title">
                  {preferenceLabels.save}
                </Typography>
                <Typography className="save-view-pref-add-card__sub">
                  Saves active filters & columns
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          /* Name/Edit this view Screen */
          <Box className="save-view-pref-name-card">
            <Box>
              <Typography className="save-view-pref-name-card__title">
                {preferenceLabels.name}
              </Typography>
              <Typography className="save-view-pref-name-card__sub">
                {preferenceLabels.nameDescription}
              </Typography>
            </Box>

            {/* Applied Filters & Columns Badges */}
            <Box className="save-view-pref-badges">
              {customerViewModeBadge && (
                <Box className="save-view-pref-badge">
                  <img src={customerViewModeBadge.icon} alt="" />
                  {customerViewModeBadge.label}
                </Box>
              )}
              <Box className="save-view-pref-badge">
                <img src={SearchFilterIcon} />
                {activeFilterCount} filters
              </Box>
              <Box className="save-view-pref-badge">
                <img src={EditColumnIcon} />
                {visibleColumnCount} columns
              </Box>
            </Box>

            {/* View Name Input */}
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Q2 North Forecast"
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value.slice(0, 25))}
              inputProps={{ maxLength: 25 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      variant="caption"
                      sx={{ color: "#94A3B8", fontSize: "11px", userSelect: "none" }}
                    >
                      {newViewName.length}/25
                    </Typography>
                  </InputAdornment>
                ),
              }}
              autoFocus
              disabled={isSavingPreferences}
              className="save-view-pref-input"
            />

            {/* Actions */}
            <Box className="save-view-pref-actions">
              <Button
                variant="text"
                onClick={() => {
                  setSavedViewStep("list");
                }}
                disabled={isSavingPreferences}
                className="save-view-pref-btn-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveNewView}
                disabled={!newViewName.trim() || isSavingPreferences}
                className="save-view-pref-btn-save"
              >
                {isSavingPreferences ? <CircularProgress size={16} color="inherit" /> : preferenceLabels.saveAction}
              </Button>
            </Box>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default SaveViewPreferences;
